import { clamp, Dictionary, keyBy } from 'lodash';
import { createSelector } from 'reselect';

import {
  AudioEditSlice,
  RedactedWords,
  TranscriptionViewableWords,
  ViewSettings,
} from '../models';
import { namespace } from '../store';
import { DetectionAssetState } from '../store.models';
import { findTranscriptionEditedWords } from '@redact/components/Transcription/utils';

const selectStore = (s: { [namespace]: DetectionAssetState & ViewSettings }) =>
  s[namespace];

export const selectTranscription = createSelector(
  selectStore,
  (s) => s.transcription
);

const selectMediaDuration = createSelector(
  selectStore,
  (s) => s.tdo?.details.veritoneFile?.duration
);

export const selectTranscriptionList = createSelector(
  selectTranscription,
  (t) => t?.transcription || []
);

export const selectSpeakerSegments = createSelector(
  selectTranscription,
  (t) => t?.speakerSegments
);

export const selectHasSpeakerSeparatedTranscript = createSelector(
  selectSpeakerSegments,
  (segments) => !!segments?.length
);

/**
 * Asset ids of the vtn-standard output the speaker-detection category produced
 * for this TDO, collected while the transcript was loaded.
 *
 * This is the live answer to "has speaker separation already run", as opposed
 * to the TDO's task history, which records that it once did and never stops
 * saying so — not even after the results are deleted.
 */
export const selectSpeakerSeparationAssetIds = createSelector(
  selectTranscription,
  (t) => t?.speakerSeparationAssetIds ?? []
);

export const selectTranscriptionView = createSelector(
  selectStore,
  (s) => s.transcriptionView
);

export const selectWordsSelected = createSelector(
  selectTranscriptionView,
  (s) => s.selected
);

export const selectSelectedTimeSlices = createSelector(
  selectTranscriptionView,
  ({ selected }) =>
    selected.reduce<AudioEditSlice[]>((acc, s) => {
      const lastSlice = acc.at(-1);
      if (!lastSlice || lastSlice[1] < s.startTimeMs) {
        acc.push([s.startTimeMs, s.stopTimeMs, undefined]);
        return acc;
      } else {
        const poppedSlice = acc.pop();
        if (poppedSlice) {
          acc.push([poppedSlice[0], s.stopTimeMs, lastSlice[2]]);
        }
        return acc;
      }
    }, [])
);

export const selectWordsSelectedMap = createSelector(
  selectTranscriptionView,
  (s) => keyBy(s.selected, 'id')
);

export const selectAudioRedactions = createSelector(
  selectTranscriptionView,
  (s) => s.audioRedactionEdits
);

export const selectVoiceMaskEdits = createSelector(
  selectTranscriptionView,
  (s) => s.voiceMaskEdits
);

// combines audio redactions and audio voice masks slices - may refactor later
export const selectAudioEdits = createSelector(selectTranscriptionView, (s) =>
  s.audioRedactionEdits.concat(s.voiceMaskEdits).sort((a, b) => a[0] - b[0])
);

export const selectWordsRedacted = createSelector(
  selectTranscription,
  selectTranscriptionView,
  selectMediaDuration,
  (t, s, dur) =>
    findTranscriptionEditedWords(t?.transcription, s?.audioRedactionEdits, dur)
);

export const selectWordsRedactedMap = createSelector(
  selectWordsRedacted,
  (r) => keyBy(r, 'id') as Dictionary<RedactedWords | undefined>
);

export const selectWordsVoiceMask = createSelector(
  selectTranscription,
  selectTranscriptionView,
  selectMediaDuration,
  (t, s, dur) =>
    findTranscriptionEditedWords(t?.transcription, s.voiceMaskEdits, dur)
);

export const selectWordsVoiceMaskMap = createSelector(
  selectWordsVoiceMask,
  (r) => keyBy(r, 'id') as Dictionary<RedactedWords | undefined>
);

export const selectActiveAudioEdit = createSelector(
  selectTranscriptionView,
  (v) => v.selectedAudioEditSlice
);

export const selectActiveEditWord = createSelector(
  selectTranscriptionView,
  (v) => v.selectedAudioEditWord
);

export const selectRedactNotesIsEditorOpen = createSelector(
  selectTranscriptionView,
  (v) => v.editNotesOpen
);

export const selectAudioBufferIsEditorOpen = createSelector(
  selectTranscriptionView,
  (v) => v.editBufferOpen
);

export const selectCurrentEditingWord = createSelector(
  selectTranscriptionView,
  (v) => v.currentEditWords
);

export const selectCurrentPositionWord = createSelector(
  selectTranscription,
  (t) => {
    const transcription = t?.transcription || [];
    return (currentTime: number) => {
      let firstIndex = 0;
      let lastIndex = transcription.length - 1;
      let middleIndex = clamp(
        Math.floor((lastIndex + firstIndex) / 2),
        0,
        transcription.length - 1
      );

      if (lastIndex < 0) {
        return undefined;
      }

      const compare = (item: TranscriptionViewableWords, time: number) =>
        time >= item.startTimeMs && time <= item.stopTimeMs
          ? 0
          : time < item.startTimeMs
            ? -1
            : 1;

      while (
        compare(transcription[middleIndex]!, currentTime) !== 0 && // Safe due to index conditions
        firstIndex < lastIndex
      ) {
        const middleItem = transcription[middleIndex]!; // Safe due to index conditions
        if (compare(middleItem, currentTime) < 0) {
          lastIndex = middleIndex - 1;
        } else if (compare(middleItem, currentTime) > 0) {
          firstIndex = middleIndex + 1;
        }
        middleIndex = clamp(
          Math.floor((lastIndex + firstIndex) / 2),
          0,
          transcription.length - 1
        );
      }

      return compare(transcription[middleIndex]!, currentTime) === 0 // Safe due to index conditions
        ? transcription[middleIndex]
        : undefined;
    };
  }
);
